import authApiService from "./userApiService"; // Ensure this is your axios instance with interceptors

// Helper function to handle errors
const handleError = (error) => {
  console.error('Player Profile API Error:', error);
  return {
    success: false,
    error: error.response?.data?.message || error.response?.data?.error || error.message || 'An unexpected error occurred',
    statusCode: error.response?.status,
    details: error.response?.data?.details || null
  };
};

// Helper function to handle successful responses
const handleSuccess = (response, message = null) => {
  return {
    success: true,
    data: response.data,
    message: message || response.data?.message || 'Operation completed successfully',
    statusCode: response.status
  };
};

export const playerProfileService = {

  async getProfileStats() {
    try {
      const response = await authApiService.get("/player-profiles/stats");
      return handleSuccess(response, 'Player profile statistics retrieved successfully');
    } catch (error) {
      return handleError(error);
    }
  },

  // Get active players list (from active_players table)
  async getActivePlayers() {
    try {
      const response = await authApiService.get("/player-profiles/active");
      return handleSuccess(response, 'Active players retrieved successfully');
    } catch (error) {
      return handleError(error);
    }
  },

  // Enhanced search preprocessing
  preprocessSearchTerm(searchTerm) {
    if (!searchTerm || typeof searchTerm !== 'string') {
      return '';
    }

    // Trim and normalize spaces
    let processed = searchTerm.trim().replace(/\s+/g, ' ');
    
    // Handle common name patterns
    if (processed.includes(',')) {
      // Handle "Last, First" format - convert to "First Last"
      const parts = processed.split(',').map(part => part.trim());
      if (parts.length === 2 && parts[0] && parts[1]) {
        processed = `${parts[1]} ${parts[0]}`;
      }
    }

    return processed;
  },

  // Validate profile data before sending
  validateProfileData(profileData) {
    const errors = [];

    if (!profileData.player_name || profileData.player_name.trim() === '') {
      errors.push('Player name is required');
    }

    if (profileData.player_number && typeof profileData.player_number !== 'string') {
      errors.push('Player number must be a string');
    }

    if (profileData.email && !this.isValidEmail(profileData.email)) {
      errors.push('Invalid email format');
    }

    if (profileData.phone && !this.isValidPhone(profileData.phone)) {
      errors.push('Invalid phone number format');
    }

    if (profileData.session_mins && (isNaN(parseInt(profileData.session_mins)) || parseInt(profileData.session_mins) < 0)) {
      errors.push('Session minutes must be a valid positive number');
    }

    if (profileData.cash_points && (isNaN(parseInt(profileData.cash_points)) || parseInt(profileData.cash_points) < 0)) {
      errors.push('Cash points must be a valid positive number');
    }

    if (profileData.member_since && !this.isValidDate(profileData.member_since)) {
      errors.push('Member since must be a valid date');
    }

    if (profileData.birthday && !this.isValidDate(profileData.birthday)) {
      errors.push('Birthday must be a valid date');
    }

    if (profileData.open_date && !this.isValidDate(profileData.open_date)) {
      errors.push('Open date must be a valid date');
    }

    if (profileData.closed_date && !this.isValidDate(profileData.closed_date)) {
      errors.push('Closed date must be a valid date');
    }

    return {
      isValid: errors.length === 0,
      errors: errors
    };
  },

  // Format profile data for API
  formatProfileData(profileData) {
    return {
      player_number: profileData.player_number?.trim() || null,
      player_name: profileData.player_name?.trim() || '',
      member_since: profileData.member_since || null,
      birthday: profileData.birthday || null,
      email: profileData.email?.trim() || null,
      phone: profileData.phone?.trim() || null,
      address: profileData.address?.trim() || null,
      open_date: profileData.open_date || null,
      closed_date: profileData.closed_date || null,
      type: profileData.type?.trim() || null,
      game_name: profileData.game_name?.trim() || null,
      location: profileData.location?.trim() || null,
      session_mins: profileData.session_mins ? parseInt(profileData.session_mins) : null,
      session_hrs: profileData.session_hrs?.trim() || null,
      cash_points: profileData.cash_points ? parseInt(profileData.cash_points) : null
    };
  },

  // Email validation
  isValidEmail(email) {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  },

  // Phone validation (basic)
  isValidPhone(phone) {
    const phoneRegex = /^[+]?[1-9][\d]{0,15}$/;
    return phoneRegex.test(phone.replace(/[\s\-()]/g, ''));
  },

  // Date validation
  isValidDate(dateString) {
    if (!dateString) return false;
    const date = new Date(dateString);
    return date instanceof Date && !isNaN(date);
  },

  // Enhanced getDistinctPlayers method with better search handling
  async getDistinctPlayers(page, limit, search, startDate, endDate) {
    try {
      // Preprocess the search term
      const processedSearch = this.preprocessSearchTerm(search);
      
      const params = { 
        page: parseInt(page) || 1, 
        limit: parseInt(limit) || 10 
      };
      
      // Only add search parameter if it has meaningful content
      if (processedSearch && processedSearch.length > 0) {
        params.search = processedSearch;
      }
      
      if (startDate) {
        params.startDate = startDate;
      }
      if (endDate) {
        params.endDate = endDate;
      }

      const response = await authApiService.get("/player-profiles/distinct", { 
        params,
        timeout: 30000 // 30 second timeout for large searches
      });

      // Enhanced response handling
      const result = response.data;
      
      // Add search metadata to help with debugging
      if (result.success && result.data) {
        result.searchMetadata = {
          originalSearch: search,
          processedSearch: processedSearch,
          hasActiveSearch: !!(processedSearch && processedSearch.length > 0),
          resultCount: result.data.length || 0,
          totalResults: result.pagination?.totalItems || 0
        };
      }

      return result;
    } catch (error) {
      console.error('Error in getDistinctPlayers:', error);
      
      // Enhanced error response
      return {
        success: false,
        error: error.response?.data?.error || 'Failed to fetch distinct players',
        statusCode: error.response?.status,
        searchMetadata: {
          originalSearch: search,
          processedSearch: this.preprocessSearchTerm(search),
          hasActiveSearch: !!(search && search.trim().length > 0),
          resultCount: 0,
          totalResults: 0
        },
        data: [],
        pagination: {
          currentPage: parseInt(page) || 1,
          totalPages: 0,
          totalItems: 0,
          hasNext: false,
          hasPrev: false
        }
      };
    }
  },

  // Advanced search with suggestions
  async searchPlayersWithSuggestions(searchTerm, limit = 10) {
    try {
      const processedSearch = this.preprocessSearchTerm(searchTerm);
      
      if (!processedSearch || processedSearch.length < 2) {
        return {
          success: true,
          data: [],
          suggestions: ['Try entering at least 2 characters', 'Search by name, email, phone, or player number']
        };
      }

      const response = await this.getDistinctPlayers(1, limit, processedSearch);
      
      if (response.success && response.data.length === 0) {
        // Generate search suggestions
        const suggestions = this.generateSearchSuggestions(processedSearch);
        return {
          ...response,
          suggestions
        };
      }

      return response;
    } catch (error) {
      console.error('Error in searchPlayersWithSuggestions:', error);
      return {
        success: false,
        error: 'Search failed',
        data: [],
        suggestions: ['Please try again with a different search term']
      };
    }
  },

  // Generate search suggestions
  generateSearchSuggestions(searchTerm) {
    const suggestions = [];
    
    if (searchTerm.length < 3) {
      suggestions.push('Try entering more characters for better results');
    }
    
    if (/^\d+$/.test(searchTerm)) {
      suggestions.push('Searching by player number or phone number');
    }
    
    if (searchTerm.includes('@')) {
      suggestions.push('Searching by email address');
    }
    
    if (searchTerm.includes(' ')) {
      suggestions.push('Try searching with just first name or last name');
      suggestions.push('Try reversing the name order (Last Name, First Name)');
    } else {
      suggestions.push('Try adding last name or first name');
    }
    
    suggestions.push('Check spelling and try again');
    suggestions.push('Try searching with partial name');
    
    return suggestions;
  },

  // Get all entries for a specific player
  async getPlayerEntries(playerNumber) {
    try {
      const response = await authApiService.get(`/player-profiles/entries/${playerNumber}`);
      return response.data;
    } catch (error) {
      console.error('Error getting player entries:', error);
      throw error;
    }
  },

  // Send SMS to a player
  async sendSMS(phoneNumber, message, meta = {}) {
    try {
      const response = await authApiService.post('/player-profiles/send-sms', {
        phone: phoneNumber,
        message: message,
        ...meta
      });
      return handleSuccess(response, 'SMS sent successfully');
    } catch (error) {
      return handleError(error);
    }
  },

  // Send bulk SMS to multiple players
  async sendBulkSMS(recipientsOrPhones, message) {
    const list = Array.isArray(recipientsOrPhones) ? recipientsOrPhones : [];
    const CHUNK = 100;
    const merged = {
      successCount: 0,
      skipCount: 0,
      failCount: 0,
      total: 0,
      skipped: [],
      failed: [],
      successful: [],
      results: [],
    };

    const applyChunk = (data = {}, chunkSize = 0) => {
      merged.successCount += data.successCount || 0;
      merged.skipCount += data.skipCount || 0;
      merged.failCount += data.failCount || 0;
      merged.total += data.total || chunkSize;
      merged.skipped.push(...(data.skipped || []));
      merged.failed.push(...(data.failed || []));
      merged.successful.push(...(data.successful || []));
      merged.results.push(...(data.results || []));
    };

    try {
      for (let i = 0; i < list.length; i += CHUNK) {
        const chunk = list.slice(i, i + CHUNK);
        const body = { message };
        if (typeof chunk[0] === 'string') body.phones = chunk;
        else body.recipients = chunk;

        try {
          const response = await authApiService.post('/player-profiles/send-bulk-sms', body, {
            timeout: 180000,
          });
          applyChunk(response.data?.data || {}, chunk.length);
        } catch (chunkError) {
          if (merged.successCount + merged.skipCount > 0) {
            console.error('Bulk SMS chunk failed after partial send:', chunkError);
            continue;
          }
          return handleError(chunkError);
        }
      }

      return {
        success: true,
        data: {
          success: true,
          message: merged.failCount === 0
            ? `Successfully sent ${merged.successCount} message(s)`
            : `Sent ${merged.successCount} message(s)`,
          data: merged,
        },
        message: 'Bulk SMS sent successfully',
        statusCode: 200,
      };
    } catch (error) {
      return handleError(error);
    }
  },

  // Get active players' phone numbers
  async getActivePlayersPhones() {
    try {
      const response = await authApiService.get('/player-profiles/active-phones');
      return handleSuccess(response, 'Active players phone numbers retrieved successfully');
    } catch (error) {
      return handleError(error);
    }
  },

  // Get SMS logs for dashboard (tracking)
  async getSmsLogs(params = {}) {
    try {
      const response = await authApiService.get('/player-profiles/sms-logs', { params });
      return handleSuccess(response, 'SMS logs retrieved successfully');
    } catch (error) {
      return handleError(error);
    }
  },

  async getSmsInvalidPhones(params = {}) {
    try {
      const response = await authApiService.get('/player-profiles/sms-invalid-phones', { params });
      return handleSuccess(response, 'Invalid SMS phone list retrieved successfully');
    } catch (error) {
      return handleError(error);
    }
  },

  // Unblock an invalid or unsubscribed phone number
  async unblockInvalidPhone(id) {
    try {
      const response = await authApiService.delete(`/player-profiles/sms-invalid-phones/${id}`);
      return handleSuccess(response, 'Phone number unblocked successfully');
    } catch (error) {
      return handleError(error);
    }
  },

  // Lookup custom player numbers for SMS broadcast
  async lookupPlayersForSms(playerNumbers) {
    try {
      const response = await authApiService.post('/player-profiles/lookup-players', { playerNumbers });
      return handleSuccess(response, 'Players resolved successfully');
    } catch (error) {
      return handleError(error);
    }
  },

  // Get SMS template (default template for player profiles messaging)
  async getSmsTemplate(key = 'default') {
    try {
      const response = await authApiService.get('/player-profiles/sms-template', { params: { key } });
      return handleSuccess(response, 'SMS template retrieved');
    } catch (error) {
      return handleError(error);
    }
  },

  // Save SMS template (sync to database when admin updates)
  async saveSmsTemplate(body, templateKey = 'default') {
    try {
      const response = await authApiService.put('/player-profiles/sms-template', { body, template_key: templateKey });
      return handleSuccess(response, 'SMS template saved');
    } catch (error) {
      return handleError(error);
    }
  },

  // Scheduled SMS APIs
  async getScheduledSms(params = {}) {
    try {
      const response = await authApiService.get('/player-profiles/scheduled-sms', { params });
      return handleSuccess(response, 'Scheduled SMS list retrieved');
    } catch (error) {
      return handleError(error);
    }
  },

  async createScheduledSms(payload) {
    try {
      const response = await authApiService.post('/player-profiles/scheduled-sms', payload);
      return handleSuccess(response, 'Scheduled SMS created successfully');
    } catch (error) {
      return handleError(error);
    }
  },

  async updateScheduledSms(id, payload) {
    try {
      const response = await authApiService.put(`/player-profiles/scheduled-sms/${id}`, payload);
      return handleSuccess(response, 'Scheduled SMS updated successfully');
    } catch (error) {
      return handleError(error);
    }
  },

  async deleteScheduledSms(id) {
    try {
      const response = await authApiService.delete(`/player-profiles/scheduled-sms/${id}`);
      return handleSuccess(response, 'Scheduled SMS deleted successfully');
    } catch (error) {
      return handleError(error);
    }
  },

  async cancelScheduledSms(id) {
    try {
      const response = await authApiService.patch(`/player-profiles/scheduled-sms/${id}/cancel`);
      return handleSuccess(response, 'Scheduled SMS cancelled successfully');
    } catch (error) {
      return handleError(error);
    }
  },

  async testRunScheduledSms(id) {
    try {
      const response = await authApiService.post(`/player-profiles/scheduled-sms/${id}/test-run`);
      return handleSuccess(response, 'Test run executed successfully');
    } catch (error) {
      return handleError(error);
    }
  },

  async getSmsSystemStatus() {
    try {
      const response = await authApiService.get('/player-profiles/sms-system-status');
      return handleSuccess(response, 'SMS system status retrieved');
    } catch (error) {
      return handleError(error);
    }
  },

  async getSmsProvider() {
    try {
      const response = await authApiService.get('/player-profiles/sms-provider');
      return handleSuccess(response, 'SMS provider retrieved');
    } catch (error) {
      return handleError(error);
    }
  },

  async getSmsBalance() {
    try {
      const response = await authApiService.get('/player-profiles/sms-balance');
      return handleSuccess(response, 'SMS balance retrieved');
    } catch (error) {
      return handleError(error);
    }
  },

  async setSmsProvider(provider) {
    try {
      const response = await authApiService.post('/player-profiles/sms-provider', { provider });
      return handleSuccess(response, 'SMS provider updated');
    } catch (error) {
      return handleError(error);
    }
  },

  async optimizeSmsMessage(payload) {
    try {
      const response = await authApiService.post('/player-profiles/optimize-sms', payload);
      return handleSuccess(response, 'Message optimized successfully');
    } catch (error) {
      return handleError(error);
    }
  },

  async getSmsPresets() {
    try {
      const response = await authApiService.get('/player-profiles/sms-presets');
      const payload = response.data?.data !== undefined ? response.data.data : response.data;
      return {
        success: true,
        data: payload,
        message: response.data?.message || 'Presets loaded successfully',
      };
    } catch (error) {
      return handleError(error);
    }
  },

  async createSmsPreset(presetData) {
    try {
      const response = await authApiService.post('/player-profiles/sms-presets', presetData);
      const payload = response.data?.data !== undefined ? response.data.data : response.data;
      return {
        success: true,
        data: payload,
        message: response.data?.message || 'Preset saved to database successfully',
      };
    } catch (error) {
      return handleError(error);
    }
  },

  async updateSmsPreset(id, presetData) {
    try {
      const response = await authApiService.put(`/player-profiles/sms-presets/${id}`, presetData);
      const payload = response.data?.data !== undefined ? response.data.data : response.data;
      return {
        success: true,
        data: payload,
        message: response.data?.message || 'Preset updated successfully',
      };
    } catch (error) {
      return handleError(error);
    }
  },

  async deleteSmsPreset(id) {
    try {
      const response = await authApiService.delete(`/player-profiles/sms-presets/${id}`);
      const payload = response.data?.data !== undefined ? response.data.data : response.data;
      return {
        success: true,
        data: payload,
        message: response.data?.message || 'Preset deleted successfully',
      };
    } catch (error) {
      return handleError(error);
    }
  },

  // Debounced search function for real-time search
  createDebouncedSearch(delay = 300) {
    let timeoutId;
    
    return (searchTerm, callback) => {
      clearTimeout(timeoutId);
      
      timeoutId = setTimeout(async () => {
        try {
          const result = await this.searchPlayersWithSuggestions(searchTerm);
          callback(null, result);
        } catch (error) {
          callback(error, null);
        }
      }, delay);
    };
  }
};

export default playerProfileService;